/**
 * Composable for handling spreadsheet changes and database operations
 */
import { ref } from 'vue'
import { supabase } from '@/services/supabase'
import { useStorage } from '@/composables/useStorage'
import * as XLSX from 'xlsx'
import { LogCategory, logger } from '@/services/logger'
import {systemConfigService} from '@/features/system-config'

/**
 * Interface for save operation result
 */
export interface ISaveResult {
  success: boolean
  message: string
  error?: any
  newFileId?: string
  newFileInfo?: any
  fileData?: ArrayBuffer
}

/**
 * Composable for managing spreadsheet changes and database operations
 * @returns Functions and state for handling spreadsheet changes
 */
export function useSpreadsheetChanges() {
  const isSaving = ref(false)
  const saveSuccess = ref<string | null>(null)
  const saveError = ref<string | null>(null)
  
  const { uploadFile, deleteFileById } = useStorage()
  
  /**
   * Save changes to the database
   * @param excelViewerComponent - The Excel viewer component instance
   * @param fileId - The ID of the file to update
   * @param fileInfo - Information about the file
   * @param documentId - The ID of the document to update
   * @returns Promise that resolves when the save operation is complete
   */
  const saveChangesToDb = async (
    excelViewerComponent: any,
    fileId: string,
    fileInfo: any,
    documentId: string
  ): Promise<ISaveResult> => {
    if (!excelViewerComponent || !fileId || !fileInfo) {
      saveError.value = 'Cannot save: Missing spreadsheet data or file information'
      return {
        success: false,
        message: saveError.value
      }
    }

    saveSuccess.value = null
    saveError.value = null
    isSaving.value = true

    try {
      // Get the current data from the Excel viewer
      const currentData = excelViewerComponent.getCurrentData()

      if (!currentData) {
        throw new Error('No data to save')
      }

      // Create a new workbook
      const wb = XLSX.utils.book_new()

      // Add the sheet with the current data
      const ws = XLSX.utils.aoa_to_sheet(currentData.data)
      XLSX.utils.book_append_sheet(wb, ws, currentData.currentSheet)

      // Convert workbook to binary
      const wbBinary = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })

      // Create a Blob
      const blob = new Blob([wbBinary], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

      // Create a File object
      const timestamp = Date.now()
      const originalFilename = fileInfo.name || 'spreadsheet.xlsx'
      const newFilename = `${timestamp}-${originalFilename}`
      const fileToUpload = new File([blob], newFilename, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

      // Determine the bucket and path
      const bucket = fileInfo.bucket_name || 'Templates'
      const path = `${timestamp}-${originalFilename}`

      // Use the uploadFile function from useStorage composable
      // This will try to use the admin client first to bypass RLS policies
      const uploadResult = await uploadFile(bucket, path, fileToUpload, {
        customFileName: newFilename,
        userId: (await supabase.auth.getUser()).data.user?.id,
        createDocumentEntry: false, // We'll handle document update separately
      })

      if (!uploadResult) {
        throw new Error('File upload failed: No data returned')
      }

      logger.info('File uploaded successfully using useStorage composable', LogCategory.GENERAL, {
        uploadResult,
        path: path
      })

      // Get the public URL for the file
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(path)

      // Create a new record in the files table
      const { data: newFileData, error: newFileError } = await supabase
        .from('files')
        .insert([{
          name: newFilename,
          url: publicUrl,
          type: 'xlsx',
          size: Math.round(blob.size / 1024) // Convert bytes to KB
        }])
        .select()
        .single()

      if (newFileError) {
        throw new Error(`Error creating file record: ${newFileError.message}`)
      }

      // Update the document with the new file ID
      const { error: updateError } = await supabase
        .from('documents')
        .update({
          file_id: newFileData.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', documentId)
        .select()
        .single()

      if (updateError) {
        throw new Error(`Error updating document: ${updateError.message}`)
      }

      // Store the original file ID before updating it
      const originalFileId = fileId
      
      logger.info('File saved successfully', LogCategory.DATABASE, {
        newFileId: newFileData.id,
        originalFileId: originalFileId,
        documentId: documentId
      })

      // Check system configuration before cleaning up the original file
      const shouldCleanup = await checkCleanupConfiguration()
      if (shouldCleanup) {
        logger.info('Cleanup configuration is enabled, cleaning up original file', LogCategory.GENERAL, {
          originalFileId
        })
        await cleanupOriginalFile(originalFileId);
      } else {
        logger.info('Cleanup configuration is disabled, skipping cleanup of original file', LogCategory.GENERAL, {
          originalFileId
        })
      }
      
      saveSuccess.value = 'Changes saved successfully'
      
      return {
        success: true,
        message: saveSuccess.value,
        newFileId: newFileData.id,
        newFileInfo: newFileData,
        fileData: new Uint8Array(wbBinary).buffer
      }
    } catch (error) {
      logger.error('Error saving file changes', LogCategory.DATABASE, { error })
      saveError.value = error instanceof Error ? error.message : 'An unknown error occurred while saving'
      
      return {
        success: false,
        message: saveError.value,
        error
      }
    } finally {
      isSaving.value = false
    }
  }

  /**
   * Check if original file cleanup is enabled in system configuration
   * @returns Promise<boolean> True if cleanup is enabled, false otherwise
   */
  const checkCleanupConfiguration = async (): Promise<boolean> => {
    try {
      const configData = await systemConfigService.getAllConfig();
      const cleanupOriginalFile = configData.find(item => item.property === 'cleanup_original_file');

      if (cleanupOriginalFile) {  
        const value = cleanupOriginalFile.value;
        if (value === 'true' || value === true) {
          return true; // Cleanup is enabled
        } else if (value === 'false' || value === false) {
          return false; // Cleanup is disabled
        }
      }
      // Default to false for any other value
      return false;
    } catch (error) {
      logger.warn('Exception checking cleanup configuration', LogCategory.GENERAL, { error });
      return false; // Default to false on any exception
    }
  };

  /**
   * Clean up the original file after a successful save
   * This deletes the file from storage and the database
   */
  const cleanupOriginalFile = async (originalFileId: string | null) => {
    if (!originalFileId) return
    
    try {
      logger.info('Deleting original file', LogCategory.GENERAL, {
        fileId: originalFileId
      })
      
      // Use the deleteFileById function from useStorage composable to delete the file
      await deleteFileById(originalFileId)
      
      logger.info('Original file deleted successfully', LogCategory.GENERAL, {
        fileId: originalFileId
      })
    } catch (deleteError) {
      // Log the error but don't fail the save operation
      logger.warn('Failed to delete original file', LogCategory.GENERAL, { error: deleteError })
      // We don't throw here because the main operation (saving the new file) was successful
    }
  }

  return {
    isSaving,
    saveSuccess,
    saveError,
    saveChangesToDb
  }
}